import { Component, For, Show, createMemo, createSignal } from "solid-js"
import defaultScenario from "../data/scenario/default-scenario.json"
import { isGlobalMutedAmbient, setGlobalMuteAmbient } from "../lib/ambient-audio"
import { vastandeGM } from "../lib/named-scenarios"
import { canInstallPWA, isIOS, isStandalone, triggerInstall } from "../lib/pwa-install"
import { isGlobalMutedTimeline, setGlobalMuteTimeline } from "../lib/timeline-audio"
import { useAppStore } from "../stores/app"
import { useScenarioStore } from "../stores/scenario"
import type { Agent } from "../types"
import { formatSimTime } from "../types"

// Import SVG icons
import coupleSvg from "../assets/agents/person/couple-mw.svg?raw"
import femaleSvg from "../assets/agents/person/female.svg?raw"
import settingsIcon from "../assets/agents/person/icons/settings.svg?raw"
import maleSvg from "../assets/agents/person/male.svg?raw"

const SVG_MAP: Record<string, string> = {
    "male.svg": maleSvg,
    "female.svg": femaleSvg,
    "couple-mw.svg": coupleSvg,
}

const MOBILE_SCENARIO_TEMPLATES = [
    { label: "Standard", description: "Standardscenariot", data: defaultScenario as any },
    { label: "Väntande GM", description: "Genskjutande gärningsman", data: vastandeGM },
]

interface MobileMenuProps {
    onClose: () => void
    onSelectAgent: (agent: Agent) => void
    onSelectScenario: (templateData: any) => void
}

const MobileMenu: Component<MobileMenuProps> = (props) => {
    const [scenario] = useScenarioStore()
    const [app, appActions] = useAppStore()
    const [activeSection, setActiveSection] = createSignal<"agents" | "analysis" | "settings">("agents")
    const [isMuted, setIsMuted] = createSignal(isGlobalMutedAmbient() || isGlobalMutedTimeline())

    const handleToggleMute = () => {
        const newMutedState = !isMuted()
        setIsMuted(newMutedState)
        setGlobalMuteAmbient(newMutedState)
        setGlobalMuteTimeline(newMutedState)
    }

    // Create a memo to ensure reactivity for analysis sections
    const hasAnalysis = createMemo(() => scenario.analysis.sections.length > 0)

    return (
        <>
            {/* Backdrop */}
            <Show when={app.isMobileMenuOpen}>
                <div class="fixed inset-0 z-[2000] bg-black/50 md:hidden" onClick={props.onClose} />
            </Show>

            {/* Slide-in menu */}
            <div
                class={`fixed inset-y-0 left-0 z-[2001] w-80 transform bg-white shadow-xl transition-transform duration-300 md:hidden ${
                    app.isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
                }`}
            >
                <div class="flex h-full flex-col">
                    {/* Header */}
                    <div class="flex items-center justify-between border-b border-gray-700 bg-gray-900 px-4 pb-3 text-white" style={{ "padding-top": "calc(0.75rem + env(safe-area-inset-top))" }}>
                        <h2 class="text-lg font-bold">Meny</h2>
                        <button class="rounded p-2 hover:bg-gray-800" onClick={props.onClose}>
                            ✕
                        </button>
                    </div>
                    {/* Tab Navigation */}
                    <div class="flex border-b border-gray-200">
                        <button
                            class={`flex-1 py-3 text-sm font-medium transition-colors ${
                                activeSection() === "agents"
                                    ? "border-b-2 border-blue-600 bg-blue-50 text-blue-600"
                                    : "text-gray-600 hover:bg-gray-50"
                            }`}
                            onClick={() => setActiveSection("agents")}
                        >
                            Aktörer
                        </button>

                        <Show when={hasAnalysis()}>
                            <button
                                class={`flex-1 py-3 text-sm font-medium transition-colors ${
                                    activeSection() === "analysis"
                                        ? "border-b-2 border-blue-600 bg-blue-50 text-blue-600"
                                        : "text-gray-600 hover:bg-gray-50"
                                }`}
                                onClick={() => setActiveSection("analysis")}
                            >
                                Analys
                            </button>
                        </Show>
                        <button
                            class={`flex flex-1 items-center justify-center py-3 text-sm font-medium transition-colors ${
                                activeSection() === "settings"
                                    ? "border-b-2 border-blue-600 bg-blue-50 text-blue-600"
                                    : "text-gray-600 hover:bg-gray-50"
                            }`}
                            onClick={() => setActiveSection("settings")}
                        >
                            <div class="flex h-full w-full items-center justify-center" innerHTML={settingsIcon} />
                        </button>
                    </div>{" "}
                    {/* Content */}
                    <div class="flex-1 overflow-y-auto">
                        {/* Agents Section */}
                        <Show when={activeSection() === "agents"}>
                            <div class="p-4">
                                <Show when={scenario.agents.length === 0}>
                                    <p class="py-8 text-center text-sm text-gray-500">Inga aktörer i scenariot</p>
                                </Show>
                                <For
                                    each={[...scenario.agents].sort((a, b) => {
                                        // Sort by type first
                                        if (a.type !== b.type) {
                                            const typeOrder = { person: 0, car: 1, police: 2, ambulance: 3 }
                                            return (typeOrder[a.type] ?? 99) - (typeOrder[b.type] ?? 99)
                                        }
                                        // Then sort alphabetically by name within the same type
                                        const nameA = (a.name || a.label || "").toLowerCase()
                                        const nameB = (b.name || b.label || "").toLowerCase()
                                        return nameA.localeCompare(nameB)
                                    })}
                                >
                                    {(agent) => (
                                        <button
                                            class="mb-2 flex w-full items-center gap-3 rounded-lg bg-gray-50 p-3 text-left transition-colors hover:bg-gray-100"
                                            onClick={() => {
                                                props.onSelectAgent(agent)
                                                props.onClose()
                                            }}
                                        >
                                            <div class="flex h-6 w-6 flex-shrink-0 items-center justify-center overflow-hidden">
                                                <div
                                                    style="width: 24px; height: 24px; display: flex; align-items: center; justify-content: center;"
                                                    innerHTML={(() => {
                                                        const svg = agent.svg
                                                        if (svg && SVG_MAP[svg]) {
                                                            return SVG_MAP[svg]
                                                        }
                                                        return `<span style="font-size: 20px; line-height: 1;">${agent.emoji}</span>`
                                                    })()}
                                                />
                                            </div>
                                            <div class="min-w-0 flex-1">
                                                <div class="truncate font-medium text-gray-900">
                                                    {agent.name || agent.label}
                                                </div>
                                            </div>
                                            <div class="text-gray-400">›</div>
                                        </button>
                                    )}
                                </For>
                            </div>
                        </Show>

                        {/* Analysis Section */}
                        <Show when={activeSection() === "analysis"}>
                            <div class="p-4">
                                {/* Ingress */}
                                <Show when={scenario.analysis?.ingress && scenario.analysis?.ingress.trim()}>
                                    <div class="mb-6 text-sm text-gray-700">
                                        <For each={scenario.analysis.ingress?.split("\n").filter((p) => p.trim())}>
                                            {(paragraph) => <p class="mb-2 last:mb-0">{paragraph}</p>}
                                        </For>
                                    </div>
                                </Show>{" "}
                                {/* Sections */}
                                <div class="mb-8 space-y-4">
                                    <For each={scenario.analysis?.sections}>
                                        {(section) => (
                                            <div class="">
                                                <div class="mb-2 flex items-center gap-2">
                                                    <span class="text-xs text-gray-500">
                                                        {formatSimTime(section.time)}
                                                    </span>
                                                    <span class="text-xs text-gray-400">•</span>
                                                    <span class="text-xs text-gray-500">#{section.order + 1}</span>
                                                </div>
                                                <Show when={section.headline && section.headline.trim()}>
                                                    <h3 class="mb-2 text-base font-bold text-gray-900">
                                                        {section.headline}
                                                    </h3>
                                                </Show>
                                                <div class="text-sm text-gray-700">
                                                    <For each={section.content?.split("\n").filter((p) => p.trim())}>
                                                        {(paragraph) => <p class="mb-2 last:mb-0">{paragraph}</p>}
                                                    </For>
                                                </div>
                                            </div>
                                        )}
                                    </For>
                                </div>
                                <Show when={scenario.analysis.sections.length === 0}>
                                    <p class="py-8 text-center text-sm text-gray-500">Ingen analys tillgänglig</p>
                                </Show>
                            </div>
                        </Show>

                        {/* Settings Section */}
                        <Show when={activeSection() === "settings"}>
                            <div class="p-4">
                                <div class="space-y-4">
                                    {/* New Scenario */}
                                    <div class="rounded-lg border border-gray-200 bg-gray-50 p-4">
                                        <div class="mb-3 font-medium text-gray-900">Nytt scenario</div>
                                        <div class="space-y-2">
                                            <For each={MOBILE_SCENARIO_TEMPLATES}>
                                                {(t) => (
                                                    <button
                                                        class="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3 text-left active:border-blue-400 active:bg-blue-50"
                                                        style={{ "-webkit-tap-highlight-color": "transparent" }}
                                                        onClick={() => {
                                                            props.onSelectScenario(t.data)
                                                            props.onClose()
                                                        }}
                                                    >
                                                        <div>
                                                            <div class="font-medium text-gray-900">{t.label}</div>
                                                            <div class="mt-0.5 text-xs text-gray-500">
                                                                {t.description}
                                                            </div>
                                                        </div>
                                                        <svg
                                                            class="h-4 w-4 shrink-0 text-gray-400"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                stroke-linecap="round"
                                                                stroke-linejoin="round"
                                                                stroke-width="2"
                                                                d="M9 5l7 7-7 7"
                                                            />
                                                        </svg>
                                                    </button>
                                                )}
                                            </For>
                                        </div>
                                    </div>
                                    {/* Install PWA */}
                                    <Show when={!isStandalone() && (canInstallPWA() || isIOS())}>
                                        <div class="rounded-lg border border-blue-100 bg-blue-50 p-4">
                                            <div class="mb-1 font-medium text-gray-900">Lägg till som app</div>
                                            <div class="mb-3 text-sm text-gray-500">
                                                Lägg till på hemskärmen för bästa upplevelse
                                            </div>
                                            {/* Android/Chrome: native prompt */}
                                            <Show when={canInstallPWA()}>
                                                <button
                                                    class="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 font-medium text-white active:bg-blue-700"
                                                    style={{ "-webkit-tap-highlight-color": "transparent" }}
                                                    onClick={triggerInstall}
                                                >
                                                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                    </svg>
                                                    Installera app
                                                </button>
                                            </Show>
                                            {/* iOS Safari: manual share instructions */}
                                            <Show when={isIOS() && !canInstallPWA()}>
                                                <div class="flex items-start gap-2 text-sm text-gray-600">
                                                    <span class="mt-0.5 text-lg leading-none">↑</span>
                                                    <span>Tryck på <strong>Dela</strong> i Safari och välj <strong>Lägg till på hemskärmen</strong></span>
                                                </div>
                                            </Show>
                                        </div>
                                    </Show>

                                    {/* Audio Mute Toggle */}
                                    <div class="flex items-center justify-between rounded-lg bg-gray-50 p-4">
                                        <div>
                                            <div class="font-medium text-gray-900">Ljud av</div>
                                            <div class="text-sm text-gray-500">Stäng av alla ljud</div>
                                        </div>
                                        <label class="relative inline-block h-6 w-12 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={isMuted()}
                                                onChange={handleToggleMute}
                                                class="peer sr-only"
                                            />
                                            <div class="peer h-6 w-12 rounded-full bg-gray-300 transition-colors peer-checked:bg-blue-600" />
                                            <div class="absolute top-1 left-1 h-4 w-4 rounded-full bg-white transition-transform peer-checked:translate-x-6" />
                                        </label>
                                    </div>

                                    {/* Labels Toggle */}
                                    <div class="flex items-center justify-between rounded-lg bg-gray-50 p-4">
                                        <div>
                                            <div class="font-medium text-gray-900">Visa etiketter</div>
                                            <div class="text-sm text-gray-500">Visa aktörsetiketter på kartan</div>
                                        </div>
                                        <label class="relative inline-block h-6 w-12 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={app.showLabels}
                                                onChange={() => appActions.toggleShowLabels()}
                                                class="peer sr-only"
                                            />
                                            <div class="peer h-6 w-12 rounded-full bg-gray-300 transition-colors peer-checked:bg-blue-600" />
                                            <div class="absolute top-1 left-1 h-4 w-4 rounded-full bg-white transition-transform peer-checked:translate-x-6" />
                                        </label>
                                    </div>

                                    {/* Info */}
                                    <div class="rounded-lg bg-gray-50 p-4">
                                        <div class="space-y-2 text-sm text-gray-600">
                                            <p>
                                                <span class="font-semibold">Läge:</span> Endast simulering
                                            </p>
                                            <p>
                                                <span class="font-semibold">Aktörer:</span> {scenario.agents.length}
                                            </p>
                                            <p class="mt-4 text-xs text-gray-500">
                                                Mobilversionen är optimerad för att visa simuleringar. Använd desktop
                                                för redigering.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Show>
                    </div>
                </div>
            </div>
        </>
    )
}

export default MobileMenu
